/* eslint-disable no-console */
import { useState, useCallback, useMemo, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";

import Card from "@mui/material/Card";
import PropTypes from "prop-types";
import moment from "moment";

import MDBox from "components/MDBox";
import CardTitle from "components/CardTitle";
import { useTranslation } from "react-i18next";
import ChatMessenger from "layouts/chat/chatMessenger";
import MDAppBar from "components/MDAppBar";
import chatUtils from "layouts/chat/chatUtils";

import DataTableChat from "components/DataTableChat";
import { Grid } from "@mui/material";
import {
  sendChatMessage,
  sendChatReadEvent,
  getAllChatMessages,
  deleteChatMessage,
} from "store/actions/chatActions";
import { connect } from "react-redux";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";

const { getActiveClientReservationIdsForChat } = chatUtils;

const formatWhen = (ts) => {
  if (!ts) return "";
  const date = moment(ts);
  const now = moment();
  return date.isSame(now, "day") ? date.format("HH:mm") : date.format("DD/MM/YYYY");
};

function CellActive({ value }) {
  const isActive = Boolean(value);

  return (
    <MDBox display="flex" alignItems="center" justifyContent="center">
      <MDBox
        component="span"
        title={isActive ? "Active" : "Inactive"}
        sx={{
          width: 12,
          height: 12,
          borderRadius: "50%",
          display: "inline-block",
          backgroundColor: isActive ? "#2ecc71" : "#7f8c8d",
        }}
      />
    </MDBox>
  );
}

CellActive.defaultProps = { value: false };
CellActive.propTypes = { value: PropTypes.bool };

const columns = (t, showActiveColumn) => {
  const baseColumns = [
    {
      Header: "Chat",
      id: "chat",
      accessor: (row) => row.name,
      // eslint-disable-next-line react/prop-types
      Cell({ row }) {
        // eslint-disable-next-line react/prop-types
        const { namePrimary, nameSecondary } = row.original || {};
        return (
          <div style={{ display: "flex", flexDirection: "column", maxWidth: "360px" }}>
            <div
              style={{
                lineHeight: 1.2,
                fontWeight: 400,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={namePrimary}
            >
              {namePrimary}
            </div>
            <div
              style={{
                opacity: 0.8,
                lineHeight: 1.2,
                fontWeight: 400,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={nameSecondary}
            >
              {nameSecondary}
            </div>
          </div>
        );
      },
    },
    {
      Header: "When",
      id: "lastMessage.createdAt",
      accessor: (row) => row?.lastMessage?.createdAt ?? 0,
      sortType: (rowA, rowB, columnId) =>
        (rowB.values[columnId] ?? 0) - (rowA.values[columnId] ?? 0),
      // eslint-disable-next-line react/prop-types
      Cell: ({ value }) => <span>{formatWhen(value)}</span>,
      align: "right",
    },
  ];

  if (!showActiveColumn) return baseColumns;

  return [
    {
      Header: t("active"),
      accessor: "isActive",
      Cell: CellActive,
      maxWidth: 10,
      align: "center",
    },
    ...baseColumns,
  ];
};

function Chat({
  apartmentsReducer,
  projectsReducer,
  clientsReducer,
  clientReservationsReducer,
  chatReducer,
  sendChatMessageAction,
  sendChatReadEventAction,
  actGetAllChatMessages,
  actDeleteChatMessage,
}) {
  const { t } = useTranslation();

  const [selectedClientReservationId, setSelectedClientReservationId] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    actGetAllChatMessages();
  }, []);

  const clientReservationsRaw = clientReservationsReducer?.clientReservations || [];
  const clientReservationsArray = Array.isArray(clientReservationsRaw)
    ? clientReservationsRaw
    : Object.values(clientReservationsRaw).flatMap((item) => Object.values(item || {}));

  const activeClientReservationIds = useMemo(
    () => getActiveClientReservationIdsForChat(clientReservationsArray),
    [clientReservationsArray]
  );

  const formatedData = useMemo(() => {
    const apartmentsMetaByClientReservation = chatReducer?.apartmentsMetaByClientReservation || {};

    return clientReservationsArray
      .map((clientReservation) => {
        const clientReservationId = clientReservation?.uid;
        const apartmentId = clientReservation?.apartmentId;
        const projectId = clientReservation?.projectId;
        const clientId = clientReservation?.clientId || clientReservation?.clientUserId;
        const client = clientsReducer?.[clientId] || {};
        const startDate = Number(clientReservation?.startDate);
        const endDate = Number(clientReservation?.endDate);

        const projectName = projectsReducer?.[projectId]?.name || projectId || "-";
        const apartmentName = apartmentsReducer?.[apartmentId]?.name || apartmentId || "-";
        const clientName =
          `${client?.firstName || ""} ${client?.lastName || ""}`.trim() ||
          client?.email ||
          clientId ||
          "-";
        const startLabel = Number.isFinite(startDate)
          ? moment(startDate).format("DD/MM/YYYY")
          : "-";
        const endLabel = Number.isFinite(endDate) ? moment(endDate).format("DD/MM/YYYY") : "-";
        const apartmentMeta = apartmentsMetaByClientReservation[clientReservationId] || {};

        return {
          ...clientReservation,
          ...apartmentMeta,
          clientReservationId,
          clientId,
          apartmentId,
          projectId,
          projectName,
          clientName,
          apartmentName,
          namePrimary: `${projectName} - ${clientName}`,
          nameSecondary: `${apartmentName} (${startLabel} - ${endLabel})`,
          name: `${projectName} - ${clientName} - ${apartmentName} (${startLabel} - ${endLabel})`,
          isActive: activeClientReservationIds.has(clientReservationId),
        };
      })
      .sort((a, b) =>
        (a?.name || "").toString().localeCompare((b?.name || "").toString(), undefined, {
          numeric: true,
          sensitivity: "base",
        })
      );
  }, [
    clientReservationsArray,
    chatReducer?.apartmentsMetaByClientReservation,
    chatReducer?.messagesByClientReservation,
    projectsReducer,
    apartmentsReducer,
    clientsReducer,
    activeClientReservationIds,
  ]);

  const rowsToShow = useMemo(
    () => (tabValue === 0 ? formatedData.filter((row) => row?.isActive) : formatedData),
    [formatedData, tabValue]
  );

  const selectedThread = useMemo(
    () =>
      rowsToShow.find((row) => row?.clientReservationId === selectedClientReservationId) || null,
    [rowsToShow, selectedClientReservationId]
  );

  const selectedApartmentId = selectedThread?.apartmentId || null;
  const selectedProjectId = selectedThread?.projectId || null;
  const selectedClientId = selectedThread?.clientId || null;

  const selectedIndex = useMemo(
    () => rowsToShow.findIndex((row) => row?.clientReservationId === selectedClientReservationId),
    [rowsToShow, selectedClientReservationId]
  );

  const lastReadMaster = useMemo(() => {
    if (!selectedClientReservationId) return 0;
    const meta =
      chatReducer?.apartmentsMetaByClientReservation?.[selectedClientReservationId] || {};
    return meta?.lastRead?.master || 0;
  }, [chatReducer?.apartmentsMetaByClientReservation, selectedClientReservationId]);

  const messagesByApartment = useMemo(() => {
    if (!selectedClientReservationId) return [];
    const reservationMessagesObj =
      chatReducer?.messagesByClientReservation?.[selectedClientReservationId];
    if (!reservationMessagesObj) return [];
    return Object.values(reservationMessagesObj) || [];
  }, [chatReducer?.messagesByClientReservation, selectedClientReservationId]);

  const markAsRead = useCallback(({ apartmentId, projectId, clientReservationId, clientId }) => {
    if (!apartmentId || !projectId || !clientReservationId || !clientId) return;

    sendChatReadEventAction({
      apartmentId,
      projectId,
      clientReservationId,
      clientId,
      master: Date.now(),
    });

    console.log("Marked as read", { apartmentId, projectId, clientReservationId, clientId });
  }, []);

  const handleSend = useCallback(
    (text) => {
      if (!selectedApartmentId || !selectedProjectId) return;
      if (!selectedClientReservationId) return;

      markAsRead({
        apartmentId: selectedApartmentId,
        projectId: selectedProjectId,
        clientReservationId: selectedClientReservationId,
        clientId: selectedClientId,
      });

      sendChatMessageAction({
        apartmentId: selectedApartmentId,
        projectId: selectedProjectId,
        clientReservationId: selectedClientReservationId,
        clientId: selectedClientId,
        message: {
          createdAt: Date.now(),
          text,
          id: uuidv4(),
          sender: "master",
        },
      });
    },
    [selectedApartmentId, selectedClientId, selectedProjectId, selectedClientReservationId]
  );

  const handleDelete = useCallback(
    (messageId) => {
      if (!selectedApartmentId || !selectedProjectId || !messageId) return;
      if (!selectedClientReservationId) return;

      actDeleteChatMessage({
        projectId: selectedProjectId,
        apartmentId: selectedApartmentId,
        clientReservationId: selectedClientReservationId,
        clientId: selectedClientId,
        messageId,
      });
    },
    [selectedApartmentId, selectedProjectId, selectedClientReservationId, selectedClientId]
  );

  const tabsInfo = [
    { label: t("chatWithActiveClientReservation"), icon: "event_available" },
    { label: t("chatAllExisting"), icon: "chat" },
  ];

  const titleToShow = tabValue === 0 ? t("chatWithActiveClientReservation") : t("chatAllExisting");

  // useEffect(
  //   () => () => {
  //     markAsRead({
  //       apartmentId: selectedApartmentId,
  //       projectId: selectedProjectId,
  //       clientReservationId: selectedClientReservationId,
  //       clientId: selectedClientId,
  //     });
  //   },
  //   [selectedApartmentId, selectedProjectId, selectedClientReservationId]
  // );

  return (
    <DashboardLayout>
      <DashboardNavbar title="chat" route="/" />
      <MDBox mt={3}>
        <MDAppBar
          tabsInfo={tabsInfo}
          tabValue={tabValue}
          setTabValue={(index) => setTabValue(index)}
        />
      </MDBox>
      <MDBox pt={6}>
        <Card>
          <CardTitle title={titleToShow} />

          <Grid container spacing={3} sx={{ p: 2 }}>
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
              <MDBox sx={{ height: "min(750px, 70vh)" }}>
                <DataTableChat
                  table={{ columns: columns(t, tabValue === 1), rows: rowsToShow }}
                  canSearch
                  canActuate
                  action={(row) => {
                    const clientReservationId = row?.original?.clientReservationId;

                    setSelectedClientReservationId(clientReservationId);
                  }}
                  selectedIndex={selectedIndex >= 0 ? selectedIndex : -1}
                />
              </MDBox>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
              <ChatMessenger
                messages={messagesByApartment}
                lastRead={lastReadMaster}
                onSend={handleSend}
                onDelete={handleDelete}
                onRead={() =>
                  markAsRead({
                    apartmentId: selectedApartmentId,
                    projectId: selectedProjectId,
                    clientReservationId: selectedClientReservationId,
                    clientId: selectedClientId,
                  })
                }
              />
            </Grid>
          </Grid>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

Chat.propTypes = {
  apartmentsReducer: PropTypes.object.isRequired,
  projectsReducer: PropTypes.object.isRequired,
  clientsReducer: PropTypes.object.isRequired,
  clientReservationsReducer: PropTypes.object.isRequired,
  chatReducer: PropTypes.object.isRequired,
  sendChatMessageAction: PropTypes.func.isRequired,
  sendChatReadEventAction: PropTypes.func.isRequired,
  actGetAllChatMessages: PropTypes.func.isRequired,
  actDeleteChatMessage: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  const {
    chatReducer = {},
    clientsReducer = {},
    apartmentsReducer = {},
    projectsReducer = {},
    clientReservationsReducer = {},
    mainUserReducer = {},
  } = state;

  const isProjectManager = mainUserReducer?.isProjectManager || false;
  const userProjects = mainUserReducer?.projects || {};

  let clientReservations = Object.values(
    clientReservationsReducer?.clientReservations || {}
  ).flatMap((item) => Object.values(item || {}));

  if (isProjectManager) {
    clientReservations = clientReservations.filter(
      (reservation) => userProjects?.[reservation?.projectId]
    );
  }

  return {
    chatReducer,
    clientsReducer,
    apartmentsReducer,
    projectsReducer,
    clientReservationsReducer: { clientReservations },
  };
};

const mapDispatchToProps = (dispatch) => ({
  sendChatMessageAction: (messageData) => dispatch(sendChatMessage(messageData)),
  sendChatReadEventAction: (payload) => dispatch(sendChatReadEvent(payload)),
  actGetAllChatMessages: () => dispatch(getAllChatMessages()),
  actDeleteChatMessage: (payload) => dispatch(deleteChatMessage(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Chat);
